import { useMemo } from 'react';
import type { FlowAnalyticsApiResponse, FlowEmail, FlowSummary } from '@/lib/api/email-flows/types';
import { areUpdatesDisabled, getTriggerEventName } from '../logic';
import { FLOW_TRIGGER } from '@/lib';

interface UseEmailMetadataProps {
    flowData: FlowSummary | undefined;
    emailData: FlowEmail | undefined;
    emailId?: number;
    analyticsData?: FlowAnalyticsApiResponse;
}

interface UseEmailMetadataReturn {
    emailCount: number;
    isFirstEmail: boolean;
    trigger: string;
    isFirstTimeBasedEmail: boolean;
    isCartAbandonedFlow: boolean;
    actionsDisabled: boolean;
    hasSentEmails: boolean;
    hasNewReferralTag: boolean;
    triggerEventName: string;
    canAddAnotherEmail: boolean;
}

export function useEmailMetadata({
    flowData,
    emailData,
    emailId,
    analyticsData,
}: UseEmailMetadataProps): UseEmailMetadataReturn {
    return useMemo(() => {
        const emailCount = flowData?.flow_emails?.length || 0;
        const isFirstEmail = emailId ? emailCount === 1 : emailCount === 0;
        const trigger = flowData?.trigger || flowData?.trigger_type || 'checkout';
        const isFirstTimeBasedEmail = trigger === 'time' && isFirstEmail;
        const isCartAbandonedFlow = trigger === 'cart-abandonment';

        // Check if actions should be disabled
        // 1. If email has been sent (individual email check)
        // 2. If broadcast has started (TIME-triggered flows only)
        const emailSent = emailData ? areUpdatesDisabled(emailData) : false;
        const broadcastStarted = flowData?.trigger === FLOW_TRIGGER.TIME && flowData?.data?.started === true;
        const actionsDisabled = emailSent || broadcastStarted;

        // Check if email has been sent by looking at analytics from the analytics endpoint
        // analyticsData.analytics is keyed by flow_id, and emails is keyed by flow_email_id
        const flowAnalytics = flowData?.flow_id ? analyticsData?.analytics?.[flowData.flow_id] : undefined;
        const emailAnalyticsSent = emailId ? (flowAnalytics?.emails?.[emailId]?.sent ?? 0) : 0;

        const hasSentEmails =
            emailAnalyticsSent > 0 ||
            (trigger === FLOW_TRIGGER.TIME && flowData?.data?.started === true && (flowData?.recipient_count ?? 0) > 0);

        // Check if flow has new_referral tag
        const tags = flowData?.data?.specific_time?.tags || [];
        const hasNewReferralTag = tags.some((tag: { name: string }) => tag.name === 'new_referral');

        const triggerEventName = getTriggerEventName(trigger, hasNewReferralTag);

        // Check if can add another email (cart abandoned flows max 2 emails)
        const canAddAnotherEmail = !(isCartAbandonedFlow && emailCount >= 2);

        return {
            emailCount,
            isFirstEmail,
            trigger,
            isFirstTimeBasedEmail,
            isCartAbandonedFlow,
            actionsDisabled,
            hasSentEmails,
            hasNewReferralTag,
            triggerEventName,
            canAddAnotherEmail,
        };
    }, [flowData, emailData, emailId, analyticsData]);
}
